import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet2, ShieldCheck, Pencil, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Field } from "@/pages/Login";

export default function WalletPage() {
  const [wallet, setWallet] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/wallet");
    setWallet(data.wallet_address || "");
    setDraft(data.wallet_address || "");
    setEditing(!data.wallet_address);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/wallet", { wallet_address: draft.trim() });
      setWallet(data.wallet_address);
      setEditing(false);
      toast.success("Wallet address saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Wallet Management" subtitle="Add or edit your BEP20 (BSC) withdrawal address. Only one wallet is allowed." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="glass-strong p-6 neon-glow lg:col-span-2">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
              <Wallet2 className="text-[#00F0FF]" size={20} />
            </div>
            <div>
              <div className="font-display text-lg font-bold">Withdrawal Wallet</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#00F0FF] font-bold">Network · BEP20</div>
            </div>
          </div>

          {!editing && wallet ? (
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-bold mb-2">Saved Address</div>
              <div className="flex items-center gap-3">
                <div className="font-mono text-sm bg-[#060B19] border border-white/10 rounded-lg px-3 py-3 flex-1 break-all" data-testid="wallet-saved">{wallet}</div>
                <button onClick={() => { setDraft(wallet); setEditing(true); }} data-testid="wallet-edit"
                        className="p-3 rounded-lg bg-[#121E3D] border border-white/10 hover:border-[#00F0FF]/40 transition-all flex-shrink-0">
                  <Pencil size={16} className="text-[#00F0FF]" />
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Field label="BEP20 Wallet Address" value={draft} onChange={setDraft} testid="wallet-input" placeholder="0x..." />
              <div className="flex gap-3">
                <button onClick={save} disabled={busy} data-testid="wallet-save"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-50">
                  <Check size={18} /> {busy ? "Saving…" : "Save Wallet"}
                </button>
                {wallet && (
                  <button onClick={() => { setDraft(wallet); setEditing(false); }}
                          className="px-5 py-3 rounded-lg bg-[#121E3D] border border-white/10 hover:border-[#00F0FF]/40 transition-all">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="glass p-6">
          <div className="flex items-center gap-2 text-[#00F0FF] mb-3">
            <ShieldCheck size={18} /> <span className="font-bold uppercase text-xs tracking-wider">Important</span>
          </div>
          <ul className="text-sm text-[#94A3B8] space-y-2.5 leading-relaxed">
            <li>• Use a valid <strong className="text-white">BEP20 (Binance Smart Chain)</strong> address only.</li>
            <li>• Address must start with <span className="font-mono text-[#00F0FF]">0x</span> and be 42 characters long.</li>
            <li>• Withdrawals are sent to this address only.</li>
            <li>• You can edit your address anytime on this page.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
